import React from "react";
import {
  Alert,
  Badge,
  Button,
  Code,
  Group,
  Modal,
  Stack,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { FiTrash2 } from "react-icons/fi";
import { openapi, type GetMultiplacerDrofusInterfaceRunsApiResponse } from "../../openapi";
import { useAppDispatch } from "../app/hooks";
import { setDrofusForekomsterSelectedRunId } from "./drofusForekomsterSlice";

type DrofusRun = GetMultiplacerDrofusInterfaceRunsApiResponse["items"][number];

type Props = {
  projectNo: number;
  selectedRun: DrofusRun | null | undefined;
  disabled?: boolean;
  onDeleted?: () => void;
};

function formatDateTime(value?: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("nb-NO", {
    dateStyle: "short",
    timeStyle: "medium",
  }).format(date);
}

export const DrofusDeleteRunButton: React.FC<Props> = ({
  projectNo,
  selectedRun,
  disabled = false,
  onDeleted,
}) => {
  const dispatch = useAppDispatch();
  const [opened, { open, close }] = useDisclosure(false);
  const [deleteRun, { isLoading }] =
    openapi.endpoints.postMultiplacerDrofusInterfaceDeleteRun.useMutation();

  const runId = selectedRun?._id ?? null;
  const isActiveRun = selectedRun?.status === "queued" || selectedRun?.status === "running";
  const canDelete = Boolean(runId) && !isActiveRun && !disabled;
  const expectedSnapshotCount =
    selectedRun?.result?.savedSnapshotCount ?? selectedRun?.result?.fetchedRowCount ?? null;

  const handleDelete = async () => {
    if (!runId) return;

    try {
      const result = await deleteRun({
        body: {
          projectNo,
          runId,
        },
      }).unwrap();

      dispatch(setDrofusForekomsterSelectedRunId(null));
      notifications.show({
        color: "green",
        title: "Kjøring slettet",
        message: `Slettet ${result.deletedSnapshotCount} snapshots og ${result.deletedRunCount} kjøring.`,
      });
      close();
      onDeleted?.();
    } catch (error) {
      notifications.show({
        color: "red",
        title: "Kunne ikke slette kjøring",
        message: error instanceof Error ? error.message : "Se konsollen eller backend-loggene.",
      });
    }
  };

  return (
    <>
      <Button
        size="xs"
        variant="subtle"
        color="red"
        radius="sm"
        leftSection={<FiTrash2 size={14} />}
        disabled={!canDelete}
        onClick={open}
      >
        Slett
      </Button>

      <Modal opened={opened} onClose={close} title="Slett dRofus-kjøring?" size="md" centered>
        <Stack gap="sm">
          <Alert color="red" variant="light" radius="sm">
            Dette sletter kjøringen og alle forekomst-snapshots som hører til. Handlingen kan ikke
            angres.
          </Alert>

          {selectedRun && (
            <Stack gap={4}>
              <Group gap="xs">
                <Badge variant="light" color="gray" radius="sm">
                  {selectedRun.status}
                </Badge>
                <Badge variant="light" color="gray" radius="sm">
                  {selectedRun.trigger}
                </Badge>
              </Group>
              <Text size="sm">
                Startet: <strong>{formatDateTime(selectedRun.startedAt)}</strong>
              </Text>
              <Text size="sm">
                Snapshots/rader: <strong>{expectedSnapshotCount ?? "-"}</strong>
              </Text>
              <Text size="xs" c="dimmed">
                Run ID
              </Text>
              <Code>{runId}</Code>
            </Stack>
          )}

          {isActiveRun && (
            <Alert color="yellow" variant="light" radius="sm">
              Kjøringen pågår eller ligger i kø og kan ikke slettes.
            </Alert>
          )}

          <Group justify="flex-end" gap="xs">
            <Button size="xs" variant="subtle" radius="sm" onClick={close} disabled={isLoading}>
              Avbryt
            </Button>
            <Button
              size="xs"
              color="red"
              radius="sm"
              loading={isLoading}
              disabled={!canDelete}
              onClick={() => void handleDelete()}
            >
              Slett kjøring
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
